/**
 * Browser WebRTC client for OpenAI Realtime (paid voice).
 * Ephemeral key must come from the backend — never ship OPENAI_API_KEY here.
 */
import { getApiBase } from "./backendProbe.js";

/**
 * @param {object} options
 * @param {'woman'|'man'|string} [options.preset]
 * @param {(status: string) => void} [options.onStatus]
 * @param {(role: 'user'|'assistant', text: string) => void} [options.onTranscript]
 * @param {(name: string, args: object) => Promise<object|string>|object|string} [options.onToolCall]
 * @param {(err: Error) => void} [options.onError]
 * @returns {Promise<{ stop: () => void, updateVoice: (preset: string) => void, ready: boolean }>}
 */
export async function startOpenAiRealtimeCall(options = {}) {
  const {
    preset = "woman",
    onStatus = () => {},
    onTranscript = () => {},
    onToolCall = null,
    onError = () => {},
  } = options;

  const apiBase = getApiBase();
  onStatus("Connecting");

  const tokenRes = await fetch(`${apiBase}/api/realtime/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ preset }),
  });
  const tokenData = await tokenRes.json().catch(() => ({}));
  if (!tokenRes.ok) {
    throw new Error(tokenData.error || `Realtime session failed (${tokenRes.status})`);
  }
  const ephemeralKey = tokenData.value;
  if (!ephemeralKey) {
    throw new Error("No ephemeral key returned for Realtime voice.");
  }

  const pc = new RTCPeerConnection();
  const audioEl = document.createElement("audio");
  audioEl.autoplay = true;
  audioEl.setAttribute("playsinline", "true");
  document.body.appendChild(audioEl);

  let localStream = null;
  let dataChannel = null;
  let stopped = false;
  let greeted = false;
  let currentVoice = tokenData.voice || (preset === "man" ? "ash" : "marin");
  const pendingToolCalls = new Set();

  const cleanup = () => {
    if (stopped) return;
    stopped = true;
    try {
      if (dataChannel && dataChannel.readyState === "open") dataChannel.close();
    } catch (_) {}
    try {
      pc.getSenders().forEach((s) => {
        try { s.track && s.track.stop(); } catch (_) {}
      });
      pc.close();
    } catch (_) {}
    try {
      if (localStream) localStream.getTracks().forEach((t) => t.stop());
    } catch (_) {}
    try {
      if (audioEl.parentNode) audioEl.parentNode.removeChild(audioEl);
      audioEl.srcObject = null;
    } catch (_) {}
  };

  pc.ontrack = (event) => {
    const [stream] = event.streams || [];
    if (stream) {
      audioEl.srcObject = stream;
      audioEl.play().catch(() => {});
    }
  };

  pc.onconnectionstatechange = () => {
    if (stopped) return;
    const state = pc.connectionState;
    if (state === "connected") onStatus("Listening");
    else if (state === "connecting" || state === "new") onStatus("Connecting");
    else if (state === "failed") {
      // Only "failed" is fatal — "disconnected" is often transient during ICE.
      const err = new Error("Realtime connection failed");
      cleanup();
      onError(err);
    }
  };

  localStream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });
  localStream.getTracks().forEach((track) => pc.addTrack(track, localStream));

  dataChannel = pc.createDataChannel("oai-events");

  const sendEvent = (payload) => {
    if (!dataChannel || dataChannel.readyState !== "open" || stopped) return;
    try {
      dataChannel.send(JSON.stringify(payload));
    } catch (_) {}
  };

  const finishToolCall = async (name, callId, argsText) => {
    if (!callId || pendingToolCalls.has(callId)) return;
    pendingToolCalls.add(callId);
    let args = {};
    try {
      args = argsText ? JSON.parse(argsText) : {};
    } catch (_) {
      args = { brief: String(argsText || "").trim() };
    }
    let output = { ok: false, error: "Tool handler missing" };
    try {
      if (typeof onToolCall === "function") {
        output = await onToolCall(name, args);
      }
    } catch (err) {
      output = { ok: false, error: err?.message || "Tool failed" };
    }
    const outputStr = typeof output === "string" ? output : JSON.stringify(output || {});
    sendEvent({
      type: "conversation.item.create",
      item: {
        type: "function_call_output",
        call_id: callId,
        output: outputStr,
      },
    });
    sendEvent({ type: "response.create" });
  };

  dataChannel.addEventListener("open", () => {
    onStatus("Listening");
    // One update: instructions + tools. Include type so GA Realtime accepts the payload.
    sendEvent({
      type: "session.update",
      session: {
        type: "realtime",
        instructions:
          "You are Zolaid. You build real websites on a live computer screen in this app. " +
          "When the user wants a website or says go ahead / build it / open the live screen, " +
          "you MUST call build_website_on_computer with a complete brief from the conversation. " +
          "Never say you cannot open the live screen. Never claim there is an issue starting the live build. " +
          "Never stall by asking about logos, photos, or imagery instead of calling the tool — build first. " +
          "Speak English unless the user uses another language. Do not invent the current time.",
        tools: [
          {
            type: "function",
            name: "build_website_on_computer",
            description:
              "Open Zolaid's small live computer screen and build the website the user requested.",
            parameters: {
              type: "object",
              properties: {
                brief: {
                  type: "string",
                  description: "Full website brief from the conversation.",
                },
              },
              required: ["brief"],
            },
          },
        ],
        tool_choice: "auto",
      },
    });
    if (!greeted) {
      greeted = true;
      // Small delay so session.update can apply before the greeting response.
      setTimeout(() => {
        if (stopped) return;
        sendEvent({
          type: "response.create",
          response: {
            instructions: "Greet the user once, briefly, as Zolaid in English, then listen.",
          },
        });
      }, 250);
    }
  });

  dataChannel.addEventListener("message", (event) => {
    if (stopped) return;
    let msg;
    try {
      msg = JSON.parse(event.data);
    } catch (_) {
      return;
    }
    const type = String(msg?.type || "");

    if (type === "input_audio_buffer.speech_started") {
      onStatus("Listening");
      return;
    }
    if (type === "input_audio_buffer.speech_stopped" || type === "response.created") {
      onStatus("Working");
      return;
    }
    if (type === "output_audio_buffer.started" || type === "response.output_audio.delta") {
      onStatus("Speaking");
      return;
    }
    if (
      type === "response.done" ||
      type === "output_audio_buffer.stopped" ||
      type === "response.output_audio.done"
    ) {
      onStatus("Listening");
      return;
    }

    if (
      type === "conversation.item.input_audio_transcription.completed" ||
      type === "conversation.item.input_audio_transcription.done"
    ) {
      const text = String(msg.transcript || msg.item?.content?.[0]?.transcript || "").trim();
      if (text) onTranscript("user", text);
      return;
    }

    if (
      type === "response.audio_transcript.done" ||
      type === "response.output_audio_transcript.done"
    ) {
      const text = String(msg.transcript || "").trim();
      if (text) onTranscript("assistant", text);
      return;
    }

    if (type === "response.text.done") {
      const text = String(msg.text || "").trim();
      if (text) onTranscript("assistant", text);
      return;
    }

    if (type === "response.function_call_arguments.done") {
      const name = String(msg.name || msg.item?.name || "").trim();
      const callId = String(msg.call_id || msg.item?.call_id || "").trim();
      const argsText = String(msg.arguments || msg.item?.arguments || "");
      finishToolCall(name, callId, argsText);
      return;
    }

    if (type === "response.output_item.done") {
      const item = msg.item || {};
      if (item.type === "function_call") {
        finishToolCall(
          String(item.name || "").trim(),
          String(item.call_id || "").trim(),
          String(item.arguments || "")
        );
      }
      return;
    }

    if (type === "error") {
      const message = String(msg.error?.message || msg.message || "Realtime error");
      const code = String(msg.error?.code || msg.code || "").toLowerCase();
      // Non-fatal: bad session.update / tool schema must not drop the whole call.
      const fatal =
        /auth|unauthorized|invalid_api_key|permission|not allowed|quota|billing/i.test(message)
        || /auth|unauthorized|invalid_api_key/i.test(code);
      console.warn("Realtime event error:", message);
      if (fatal) onError(new Error(message));
    }
  });

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  await new Promise((resolve) => {
    if (pc.iceGatheringState === "complete") {
      resolve();
      return;
    }
    const done = () => {
      pc.removeEventListener("icegatheringstatechange", onChange);
      resolve();
    };
    const onChange = () => {
      if (pc.iceGatheringState === "complete") done();
    };
    pc.addEventListener("icegatheringstatechange", onChange);
    setTimeout(done, 1500);
  });

  const sdp = pc.localDescription?.sdp || offer.sdp;
  const sdpResponse = await fetch("https://api.openai.com/v1/realtime/calls", {
    method: "POST",
    body: sdp,
    headers: {
      Authorization: `Bearer ${ephemeralKey}`,
      "Content-Type": "application/sdp",
    },
  });

  if (!sdpResponse.ok) {
    const errText = await sdpResponse.text().catch(() => "");
    cleanup();
    throw new Error(errText || `Realtime WebRTC handshake failed (${sdpResponse.status})`);
  }

  const answerSdp = await sdpResponse.text();
  await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });

  const voiceForPreset = (nextPreset) => (nextPreset === "man" ? "ash" : "marin");

  return {
    ready: true,
    stop: cleanup,
    updateVoice: (nextPreset) => {
      const voice = voiceForPreset(nextPreset);
      if (voice === currentVoice) return;
      currentVoice = voice;
      sendEvent({
        type: "session.update",
        session: {
          audio: {
            output: { voice },
          },
        },
      });
    },
  };
}
